import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const SUPPORT_EMAIL = 'info@betterchoice.live';
const TOPICS = ['signin', 'account', 'other'];
const MESSAGE_LIMIT = 1000;

async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.left = '-9999px';
  document.body.appendChild(area);
  area.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(area);
  if (!ok) throw new Error('copy failed');
}

function ContactSupportPage() {
  const { isDarkMode, toggleTheme, themeClasses } = useTheme();
  const navigate = useNavigate();
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';

  const [replyEmail, setReplyEmail] = useState('');
  const [topic, setTopic] = useState('signin');
  const [message, setMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [messageError, setMessageError] = useState('');
  const [copied, setCopied] = useState(null);
  const [copyError, setCopyError] = useState('');
  const [isCopying, setIsCopying] = useState(false);
  const [isOffline, setIsOffline] = useState(typeof navigator !== 'undefined' && navigator.onLine === false);

  useEffect(() => {
    const handleStorageChange = () => {
      const next = localStorage.getItem('language') || 'english';
      setLanguage(next);
      document.documentElement.dir = next === 'hebrew' ? 'rtl' : 'ltr';
      document.documentElement.lang = next === 'hebrew' ? 'he' : 'en';
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    if (!copied) return undefined;
    const id = window.setTimeout(() => setCopied(null), 2500);
    return () => window.clearTimeout(id);
  }, [copied]);

  const toggleLanguage = () => {
    const next = language === 'english' ? 'hebrew' : 'english';
    localStorage.setItem('language', next);
    setLanguage(next);
    document.documentElement.dir = next === 'hebrew' ? 'rtl' : 'ltr';
    document.documentElement.lang = next === 'hebrew' ? 'he' : 'en';
    window.dispatchEvent(new Event('languageChange'));
  };

  const translations = {
    english: {
      'brand.name': 'BetterChoice',
      'brand.portal': 'Dietitian Portal',
      'contact.title': 'Contact support',
      'contact.subtitle': 'Copy the address below, then email us. Do not send your password.',
      'contact.addressLabel': 'Support email',
      'contact.copyAddress': 'Copy email address',
      'contact.copying': 'Copying…',
      'contact.copied': 'Copied',
      'contact.openMail': 'Open email app',
      'contact.noteTitle': 'Or copy a ready message',
      'contact.replyEmail': 'Your email',
      'contact.replyHint': 'So we know where to reply.',
      'contact.topic': 'What you need',
      'contact.topic.signin': 'Can’t sign in',
      'contact.topic.account': 'Account',
      'contact.topic.other': 'Something else',
      'contact.message': 'What happened',
      'contact.messagePlaceholder': 'What you were trying to do, and what you saw instead.',
      'contact.copyMessage': 'Copy message',
      'contact.emailInvalid': 'This email is missing an @. Add it so we can reply.',
      'contact.messageRequired': 'Write a short note so we know what to look at.',
      'contact.copyFailed': 'Copy didn’t work. Select the text below and copy it yourself.',
      'contact.copiedAddress': 'Address copied. Paste it into a new email.',
      'contact.copiedMessage': 'Message copied. Paste it into an email to this address.',
      'contact.offline': 'You’re offline. Copy the address now and send when you’re back.',
      'contact.backToLogin': 'Back to sign in',
      'contact.passwordHint': 'Never include your password.',
      'footer.copyright': '© 2026 BetterChoice. All rights reserved.',
    },
    hebrew: {
      'brand.name': 'BetterChoice',
      'brand.portal': 'פורטל דיאטנים',
      'contact.title': 'צור קשר עם התמיכה',
      'contact.subtitle': 'העתיקו את הכתובת למטה ושלחו לנו מייל. אל תשלחו את הסיסמה.',
      'contact.addressLabel': 'אימייל תמיכה',
      'contact.copyAddress': 'העתק כתובת אימייל',
      'contact.copying': 'מעתיק…',
      'contact.copied': 'הועתק',
      'contact.openMail': 'פתח אפליקציית דואר',
      'contact.noteTitle': 'או העתיקו הודעה מוכנה',
      'contact.replyEmail': 'האימייל שלך',
      'contact.replyHint': 'כדי שנדע לאן להשיב.',
      'contact.topic': 'במה מדובר',
      'contact.topic.signin': 'לא מצליח להתחבר',
      'contact.topic.account': 'חשבון',
      'contact.topic.other': 'משהו אחר',
      'contact.message': 'מה קרה',
      'contact.messagePlaceholder': 'מה ניסית לעשות, ומה הופיע במקום.',
      'contact.copyMessage': 'העתק הודעה',
      'contact.emailInvalid': 'חסר כאן הסימן @. הוסיפו אותו כדי שנוכל להשיב.',
      'contact.messageRequired': 'כתבו כמה מילים כדי שנדע במה לטפל.',
      'contact.copyFailed': 'ההעתקה לא עבדה. סמנו את הטקסט למטה והעתיקו ידנית.',
      'contact.copiedAddress': 'הכתובת הועתקה. הדביקו אותה במייל חדש.',
      'contact.copiedMessage': 'ההודעה הועתקה. הדביקו אותה במייל לכתובת הזו.',
      'contact.offline': 'אין חיבור לרשת. העתיקו את הכתובת עכשיו ושלחו כשתחזרו.',
      'contact.backToLogin': 'חזרה להתחברות',
      'contact.passwordHint': 'לעולם אל תכללו את הסיסמה.',
      'footer.copyright': '© 2026 BetterChoice. כל הזכויות שמורות.',
    },
  };
  const t = (key) => translations[language]?.[key] || key;

  const subjectForTopic = {
    signin: language === 'hebrew' ? 'פורטל דיאטנים: לא מצליח להתחבר' : 'Dietitian portal: can’t sign in',
    account: language === 'hebrew' ? 'פורטל דיאטנים: עזרה בחשבון' : 'Dietitian portal: account help',
    other: language === 'hebrew' ? 'פורטל דיאטנים: תמיכה' : 'Dietitian portal: support',
  };

  const composedMessage = () => {
    const topicLabel = t(`contact.topic.${topic}`);
    return [
      `To: ${SUPPORT_EMAIL}`,
      `Subject: ${subjectForTopic[topic]}`,
      '',
      `From: ${replyEmail.trim()}`,
      `Topic: ${topicLabel}`,
      '',
      message.trim(),
    ].join('\n');
  };

  const handleCopy = async (kind, text) => {
    setCopyError('');
    setIsCopying(true);
    try {
      await copyToClipboard(text);
      setCopied(kind);
    } catch (err) {
      console.error('Copy failed', err);
      setCopyError(text);
    } finally {
      setIsCopying(false);
    }
  };

  const validateEmail = (value) => {
    if (!value.includes('@') || !value.includes('.')) {
      return t('contact.emailInvalid');
    }
    return '';
  };

  const handleCopyMessage = async (e) => {
    e.preventDefault();
    const nextEmailError = validateEmail(replyEmail.trim());
    const nextMessageError = message.trim() ? '' : t('contact.messageRequired');
    setEmailError(nextEmailError);
    setMessageError(nextMessageError);
    if (nextEmailError || nextMessageError) return;
    await handleCopy('message', composedMessage());
  };

  const inputClasses = 'auth-field w-full px-4 py-3 bg-moss text-ink border border-line rounded-lg placeholder:text-ink-muted caret-ink focus:outline-none focus:ring-2 focus:ring-forest focus:border-forest transition-all';
  const labelClasses = `block text-sm font-medium ${themeClasses.textSecondary} mb-2`;
  const mailtoHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subjectForTopic[topic])}`;
  const approachingLimit = message.length >= MESSAGE_LIMIT - 200;

  return (
    <div
      className={`min-h-screen ${themeClasses.bgPrimary} flex items-center justify-center px-4 py-12 transition-colors duration-300`}
      dir={direction}
    >
      <div className="fixed top-4 right-4 z-50 flex gap-2">
        <button
          type="button"
          onClick={toggleTheme}
          className={`${isDarkMode ? 'bg-surface-dark' : 'bg-surface'} ${isDarkMode ? 'text-yellow-300' : 'text-yellow-600'} p-3 rounded-lg hover:bg-moss transition-colors duration-120 border border-line min-h-[44px] min-w-[44px] focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 active:scale-[0.98]`}
          aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDarkMode ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>
        <button
          type="button"
          onClick={toggleLanguage}
          className={`${isDarkMode ? 'bg-surface-dark' : 'bg-surface'} ${isDarkMode ? 'text-forest-ondark' : 'text-forest'} p-3 rounded-lg hover:bg-moss transition-colors duration-120 border border-line font-semibold min-h-[44px] min-w-[44px] focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 active:scale-[0.98]`}
          aria-label={language === 'english' ? 'Switch to Hebrew' : 'Switch to English'}
        >
          {language === 'english' ? 'ע' : 'EN'}
        </button>
      </div>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 mb-4">
            <img src="/logo.png" alt="BetterChoice Logo" className="w-full h-full object-contain" />
          </div>
          <h1 className={`font-display text-4xl font-bold ${themeClasses.textPrimary} mb-2`}>{t('brand.name')}</h1>
          <p className={`${isDarkMode ? 'text-forest-ondark' : 'text-forest-hover'} text-lg font-medium`}>{t('brand.portal')}</p>
        </div>

        <div className={`${themeClasses.bgCard} rounded-lg p-8 border ${themeClasses.borderPrimary}`}>
          <h2 className={`text-2xl font-semibold ${themeClasses.textPrimary} mb-2 text-center`}>{t('contact.title')}</h2>
          <p className={`text-sm ${themeClasses.textSecondary} mb-6 text-center`}>{t('contact.subtitle')}</p>

          {isOffline && (
            <div className="mb-4 p-3 border rounded-lg text-sm bg-[#fffbeb] border-[#fcd34d] text-[#d97706] dark:bg-[rgba(245,158,11,0.12)] dark:border-[rgba(245,158,11,0.35)] dark:text-[#F59E0B]">
              {t('contact.offline')}
            </div>
          )}

          {copied === 'address' && (
            <div className="mb-4 p-3 bg-forest-faint border border-forest text-forest-ondark rounded-lg text-sm" role="status">
              {t('contact.copiedAddress')}
            </div>
          )}
          {copied === 'message' && (
            <div className="mb-4 p-3 bg-forest-faint border border-forest text-forest-ondark rounded-lg text-sm" role="status">
              {t('contact.copiedMessage')}
            </div>
          )}
          {copyError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-500/20 border border-red-300 dark:border-red-500/50 text-red-700 dark:text-red-300 rounded-lg text-sm" role="alert">
              <p className="mb-2">{t('contact.copyFailed')}</p>
              <textarea readOnly value={copyError} className={`${inputClasses} min-h-[6rem] text-xs`} />
            </div>
          )}

          <div className="mb-6">
            <p className={labelClasses}>{t('contact.addressLabel')}</p>
            <p className={`font-medium ${themeClasses.textPrimary} mb-3 break-all`}>{SUPPORT_EMAIL}</p>
            <button
              type="button"
              onClick={() => handleCopy('address', SUPPORT_EMAIL)}
              disabled={isCopying}
              className={`w-full min-h-[44px] ${themeClasses.btnPrimary} font-semibold py-3 px-4 rounded-md transition-colors duration-120 focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isCopying && !copied ? t('contact.copying') : copied === 'address' ? t('contact.copied') : t('contact.copyAddress')}
            </button>
            <a
              href={mailtoHref}
              className={`mt-3 flex min-h-[44px] items-center justify-center w-full border border-line bg-transparent ${themeClasses.textPrimary} font-medium py-3 px-4 rounded-md hover:bg-moss transition-colors duration-120 focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 active:scale-[0.98]`}
            >
              {t('contact.openMail')}
            </a>
          </div>

          <form onSubmit={handleCopyMessage} className="space-y-5 border-t border-rule pt-6">
            <h3 className={`text-sm font-semibold ${themeClasses.textPrimary}`}>{t('contact.noteTitle')}</h3>

            <fieldset>
              <legend className={labelClasses}>{t('contact.topic')}</legend>
              <div className="grid grid-cols-3 gap-1 rounded-md border border-line p-1">
                {TOPICS.map((id) => {
                  const selected = topic === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setTopic(id)}
                      className={`min-h-[40px] px-2 py-2 text-xs font-medium rounded-sm transition-colors duration-120 focus:outline-none focus:ring-2 focus:ring-forest ${
                        selected
                          ? 'bg-forest-faint text-forest-ondark'
                          : `${themeClasses.textMuted} hover:bg-moss`
                      }`}
                      aria-pressed={selected}
                    >
                      {t(`contact.topic.${id}`)}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <div>
              <label htmlFor="replyEmail" className={labelClasses}>{t('contact.replyEmail')}</label>
              <input
                id="replyEmail"
                type="email"
                autoComplete="email"
                inputMode="email"
                value={replyEmail}
                onChange={(e) => {
                  setReplyEmail(e.target.value);
                  if (emailError) setEmailError('');
                }}
                onBlur={() => {
                  if (replyEmail.trim()) setEmailError(validateEmail(replyEmail.trim()));
                }}
                className={inputClasses}
                placeholder="dietitian@betterchoice.one"
                aria-invalid={Boolean(emailError)}
                aria-describedby={emailError ? 'replyEmail-error' : 'replyEmail-hint'}
              />
              <p id="replyEmail-hint" className={`mt-1 text-xs ${themeClasses.textMuted}`}>{t('contact.replyHint')}</p>
              {emailError && (
                <p id="replyEmail-error" className="mt-1 text-xs text-red-600 dark:text-red-300">{emailError}</p>
              )}
            </div>

            <div>
              <label htmlFor="supportMessage" className={labelClasses}>{t('contact.message')}</label>
              <textarea
                id="supportMessage"
                value={message}
                maxLength={MESSAGE_LIMIT}
                onChange={(e) => {
                  setMessage(e.target.value);
                  if (messageError) setMessageError('');
                }}
                rows={4}
                className={`${inputClasses} resize-y min-h-[6rem]`}
                placeholder={t('contact.messagePlaceholder')}
                aria-invalid={Boolean(messageError)}
                aria-describedby={messageError ? 'supportMessage-error' : 'supportMessage-hint'}
              />
              <div className="mt-1 flex justify-between gap-3 text-xs">
                <p id="supportMessage-hint" className={themeClasses.textMuted}>{t('contact.passwordHint')}</p>
                {approachingLimit && (
                  <p className={themeClasses.textMuted}>{message.length}/{MESSAGE_LIMIT}</p>
                )}
              </div>
              {messageError && (
                <p id="supportMessage-error" className="mt-1 text-xs text-red-600 dark:text-red-300">{messageError}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isCopying}
              className={`w-full min-h-[44px] border border-line ${themeClasses.textPrimary} font-semibold py-3 px-4 rounded-md hover:bg-moss transition-colors duration-120 focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isCopying ? t('contact.copying') : copied === 'message' ? t('contact.copied') : t('contact.copyMessage')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigate('/')}
              className={`text-sm font-semibold ${isDarkMode ? 'text-forest-ondark' : 'text-forest hover:text-forest-hover'} transition-colors duration-120 focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 rounded-sm`}
            >
              {t('contact.backToLogin')}
            </button>
          </div>
        </div>

        <div className={`mt-8 text-center text-sm ${themeClasses.textMuted}`}>
          <p>{t('footer.copyright')}</p>
        </div>
      </div>
    </div>
  );
}

export default ContactSupportPage;
